import { usePage } from "@inertiajs/react";
import { Alert, Button, Stack } from "@mui/material";
import Typography from "@mui/material/Typography";
import HttpStatus from "http-status-codes";
import { FormEvent, useState } from "react";

import Input, { ChangeHandler, InputType } from "../components/forms/Input";
import SignInForm from "../components/forms/SignInForm";
import ModalContent from "../components/modal/ModalContent";
import { SharedModalProps } from "../components/modal/ModalContextProvider";
import useFetch, { FailureHandler, SuccessHandler } from "../hooks/useFetch";
import useModalDispatch from "../hooks/useModalDispatch";

import PaymentSuccessModal from "./PaymentSuccessModal";

const initialValues = {
  sponsor_name: "",
  sponsor_email: "",
};

const PaymentModal = ({
  compensationId,
  handleClose,
  onFailure,
  onSuccess,
  userName,
}: SharedModalProps<{
  compensationId: number;
  onFailure: () => void;
  onSuccess: () => void;
  userName: string;
}>) => {
  const { baseUrl } = usePage().props;
  const [loginForm, setLoginForm] = useState(false);
  const dispatchSuccessModal = useModalDispatch(PaymentSuccessModal);
  const handleSuccess: SuccessHandler = () => {
    onSuccess();
    dispatchSuccessModal();
  };
  const handleFailure: FailureHandler = ({ response }) => {
    if (response?.status === HttpStatus.UNAUTHORIZED) {
      setLoginForm(true);
    } else if (response?.status === HttpStatus.UNPROCESSABLE_ENTITY) {
      onFailure();
    }
  };
  const [post, { error, loading }] = useFetch(
    `${baseUrl}/compensations/${compensationId}/payments`,
    { method: "POST", onFailure: handleFailure, onSuccess: handleSuccess },
  );
  const [values, setValues] = useState(initialValues);

  const submitForm = () =>
    post({
      payment: values,
    });

  const handleSubmit = (e: FormEvent) => {
    e?.preventDefault();
    submitForm();
  };

  const handleChange: ChangeHandler = (e) => {
    const { name, value } = e.target;
    setValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  if (loginForm) {
    return (
      <ModalContent label="Inloggen" onClose={handleClose}>
        <Stack spacing={1}>
          <SignInForm
            onSuccess={() => {
              submitForm();
              setLoginForm(false);
            }}
          />
        </Stack>
      </ModalContent>
    );
  }

  return (
    <ModalContent
      label={`Reiskosten van ${userName} vergoeden`}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit}>
        <Stack spacing="1em" sx={{ marginBottom: "1em" }}>
          <Typography>Bedankt dat je deze reiskosten wil vergoeden!</Typography>
          <Typography>
            Na afloop van de demonstratie zal je via e-mail het bewijs van
            deelname en de bankgegevens van {userName} ontvangen. Je kan het
            bedrag dan zelf overmaken. Daarna zullen we je gegevens verwijderen.
          </Typography>
        </Stack>
        <Stack spacing={1} sx={{ marginTop: "1em" }}>
          <Input
            autoFocus
            required
            label="Hoe wil je genoemd worden?"
            name="sponsor_name"
            placeholder="Naam"
            type={InputType.Text}
            value={values.sponsor_name}
            onChange={handleChange}
          />
          <Typography marginTop="1em!important" variant="body2">
            Solidaritijd verbindt je met {userName} zodat je de reiskosten kan
            vergoeden. De deelname aan de demonstratie zal worden bevestigd via
            een foto van een los treinkaartje of een declaratieoverzicht van de
            OV-chipkaart. Om het aanvragen van een vergoeding zo laagdrempelig
            en toegankelijk mogelijk te houden, doen wij verder geen controle of{" "}
            {userName} daadwerkelijk heeft deelgenomen.{" "}
            <a
              href="https://solidaritijd.nl/over"
              rel="noreferrer"
              target="_blank"
            >
              Lees meer
            </a>
            .
          </Typography>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ marginTop: "1em" }}>
            {error}
          </Alert>
        )}
        <Stack direction="row" justifyContent="end">
          <Button
            loading={loading}
            sx={{ marginTop: "1em" }}
            type="submit"
            variant="contained"
          >
            Verstuur
          </Button>
        </Stack>
      </form>
    </ModalContent>
  );
};

export default PaymentModal;
